import { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { toast } from "sonner";
import { ArrowLeft, Eye, ImagePlus, Loader2, X } from "lucide-react";
import { adminApi } from "@/lib/blogApi";
import { formatDate } from "@/components/blog/shared";

const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

const EMPTY = {
  title: "", slug: "", excerpt: "", content: "", featured_image_url: "",
  featured_image_alt: "", author_name: "VOVERON Team", category_id: null,
  tags: [], status: "draft", is_featured: false, published_at: null,
  seo_title: "", meta_description: "", og_image_url: "",
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PreviewModal = ({ form, onClose }) => (
  <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm overflow-y-auto" data-testid="preview-modal">
    <div className="max-w-3xl mx-auto my-10 bg-[#09090b] border border-[#27272A] relative">
      <button onClick={onClose} className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white z-10" data-testid="preview-close">
        <X size={20} />
      </button>
      <div className="p-8 md:p-12">
        <p className="font-heading text-xs tracking-[0.3em] uppercase text-[#A855F7] mb-4">Preview</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-white leading-tight mb-4">{form.title || "Untitled"}</h1>
        <p className="text-[#A1A1AA] mb-2">{form.excerpt}</p>
        <p className="text-sm text-[#A1A1AA] mb-8">By {form.author_name} · {formatDate(form.published_at || new Date().toISOString())}</p>
        {form.featured_image_url && (
          <img src={form.featured_image_url} alt={form.featured_image_alt} className="w-full aspect-[21/9] object-cover border border-[#27272A] mb-8" />
        )}
        <div className="article-prose" dangerouslySetInnerHTML={{ __html: form.content }} />
      </div>
    </div>
  </div>
);

export default function ArticleEditor({ token, articleId, categories, onBack, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [savedId, setSavedId] = useState(articleId);
  const [slugTouched, setSlugTouched] = useState(!!articleId);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(!!articleId);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const quillRef = useRef(null);

  // Fetch the article exactly ONCE when the editor opens.
  // Deliberately not depending on token/onBack: parent re-renders (e.g. Supabase
  // token refresh when switching browser tabs) must never reload the draft.
  useEffect(() => {
    if (!articleId) return;
    let cancelled = false;
    adminApi.getArticle(token, articleId).then((a) => {
      if (cancelled) return;
      setForm({ ...EMPTY, ...a });
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      toast.error("Failed to load article");
      onBack();
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  // Warn before closing/reloading the page with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const set = (key, value) => {
    setDirty(true);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onTitleChange = (v) => {
    setDirty(true);
    setForm((f) => ({ ...f, title: v, slug: slugTouched ? f.slug : slugify(v) }));
  };

  const handleBack = () => {
    if (dirty && !window.confirm("You have unsaved changes. Discard them and leave the editor?")) return;
    onBack();
  };

  const uploadTo = async (file, key) => {
    setUploading(true);
    try {
      const { url } = await adminApi.uploadImage(token, file);
      set(key, url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "link", "image"],
        ["clean"],
      ],
      handlers: {
        image: function () {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            try {
              const { url } = await adminApi.uploadImage(token, file);
              const quill = quillRef.current?.getEditor();
              const range = quill?.getSelection(true);
              quill?.insertEmbed(range?.index ?? 0, "image", url);
            } catch {
              toast.error("Image upload failed");
            }
          };
          input.click();
        },
      },
    },
  }), [token]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.status === "scheduled" && !form.published_at) return toast.error("Pick a publication date for scheduled articles");
    setSaving(true);
    const payload = { ...form, category_id: form.category_id || null };
    try {
      if (savedId) {
        const res = await adminApi.updateArticle(token, savedId, payload);
        setForm((f) => ({ ...f, slug: res.slug }));
        toast.success("Article saved");
      } else {
        const res = await adminApi.createArticle(token, payload);
        setSavedId(res.id);
        setForm((f) => ({ ...f, slug: res.slug }));
        toast.success("Article created");
      }
      setDirty(false);
      onSaved();
      onBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-20 text-center text-[#A1A1AA]">Loading article…</div>;

  return (
    <div data-testid="article-editor">
      {showPreview && <PreviewModal form={form} onClose={() => setShowPreview(false)} />}

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <button onClick={handleBack} className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white" data-testid="editor-back-button">
          <ArrowLeft size={16} /> All articles
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-[#FBBF24] font-heading uppercase tracking-widest" data-testid="editor-unsaved-indicator">Unsaved changes</span>}
          <button onClick={() => setShowPreview(true)} className="cat-pill inline-flex items-center gap-2" data-testid="editor-preview-button">
            <Eye size={14} /> Preview
          </button>
          <button onClick={save} disabled={saving} className="btn-primary px-6 py-2.5 text-sm font-semibold" data-testid="editor-save-button">
            {saving ? "Saving…" : savedId ? "Save Changes" : "Create Article"}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          <div>
            <label className="admin-label">Title *</label>
            <input className="admin-input text-lg font-heading" value={form.title} onChange={(e) => onTitleChange(e.target.value)} placeholder="Article title" data-testid="editor-title-input" />
          </div>
          <div>
            <label className="admin-label">Slug (URL)</label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-[#A1A1AA] shrink-0">/blog/</span>
              <input className="admin-input" value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} data-testid="editor-slug-input" />
            </div>
          </div>
          <div>
            <label className="admin-label">Excerpt (short summary)</label>
            <textarea className="admin-input resize-none" rows={3} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="One or two sentences shown on the listing page" data-testid="editor-excerpt-input" />
          </div>
          <div>
            <label className="admin-label">Article content</label>
            <div className="quill-dark">
              <ReactQuill ref={quillRef} theme="snow" value={form.content} onChange={(v) => set("content", v)} modules={modules} placeholder="Write your article…" />
            </div>
          </div>

          {/* SEO */}
          <div className="border border-[#27272A] p-6 space-y-5 bg-[#0A0A0B]">
            <p className="font-heading text-sm font-bold text-white uppercase tracking-widest">SEO</p>
            <div>
              <label className="admin-label">SEO title <span className="normal-case">({(form.seo_title || "").length}/60)</span></label>
              <input className="admin-input" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} placeholder={form.title ? `${form.title} | VOVERON Blog` : "Custom title for search engines"} data-testid="editor-seo-title-input" />
            </div>
            <div>
              <label className="admin-label">Meta description <span className="normal-case">({(form.meta_description || "").length}/160)</span></label>
              <textarea className="admin-input resize-none" rows={2} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} data-testid="editor-meta-description-input" />
            </div>
            <div>
              <label className="admin-label">Open Graph image (social sharing)</label>
              <div className="flex items-center gap-3">
                <input className="admin-input" value={form.og_image_url} onChange={(e) => set("og_image_url", e.target.value)} placeholder="Defaults to featured image" data-testid="editor-og-image-input" />
                {form.featured_image_url && (
                  <button className="cat-pill shrink-0" onClick={() => set("og_image_url", form.featured_image_url)} data-testid="editor-og-use-featured">
                    Use featured
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="border border-[#27272A] p-6 space-y-5 bg-[#0A0A0B]">
            <p className="font-heading text-sm font-bold text-white uppercase tracking-widest">Publishing</p>
            <div>
              <label className="admin-label">Status</label>
              <select className="admin-input" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="editor-status-select">
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="published">Published</option>
              </select>
            </div>
            {(form.status === "scheduled" || form.status === "published") && (
              <div>
                <label className="admin-label">Publication date</label>
                <input
                  type="datetime-local"
                  className="admin-input"
                  value={toLocalInput(form.published_at)}
                  onChange={(e) => set("published_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
                  data-testid="editor-published-at-input"
                />
                {form.status === "published" && !form.published_at && (
                  <p className="text-xs text-[#A1A1AA] mt-1.5">Leave empty to publish now.</p>
                )}
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => set("is_featured", e.target.checked)} className="accent-[#A855F7] w-4 h-4" data-testid="editor-featured-checkbox" />
              <span className="text-sm text-white">Featured article</span>
            </label>
          </div>

          <div className="border border-[#27272A] p-6 space-y-5 bg-[#0A0A0B]">
            <p className="font-heading text-sm font-bold text-white uppercase tracking-widest">Featured image</p>
            {form.featured_image_url ? (
              <div className="relative group">
                <img src={form.featured_image_url} alt={form.featured_image_alt} className="w-full aspect-video object-cover border border-[#27272A]" data-testid="editor-featured-image-preview" />
                <button className="absolute top-2 right-2 bg-black/70 p-1.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => set("featured_image_url", "")} data-testid="editor-remove-featured-image">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label className="border border-dashed border-[#27272A] hover:border-[#A855F7] transition-colors aspect-video flex flex-col items-center justify-center gap-2 cursor-pointer text-[#A1A1AA]" data-testid="editor-featured-image-upload">
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                <span className="text-xs">{uploading ? "Uploading…" : "Upload image"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadTo(e.target.files[0], "featured_image_url")} />
              </label>
            )}
            <div>
              <label className="admin-label">Alt text (accessibility & SEO)</label>
              <input className="admin-input" value={form.featured_image_alt} onChange={(e) => set("featured_image_alt", e.target.value)} placeholder="Describe the image" data-testid="editor-image-alt-input" />
            </div>
          </div>

          <div className="border border-[#27272A] p-6 space-y-5 bg-[#0A0A0B]">
            <p className="font-heading text-sm font-bold text-white uppercase tracking-widest">Organization</p>
            <div>
              <label className="admin-label">Author</label>
              <input className="admin-input" value={form.author_name} onChange={(e) => set("author_name", e.target.value)} data-testid="editor-author-input" />
            </div>
            <div>
              <label className="admin-label">Category</label>
              <select className="admin-input" value={form.category_id || ""} onChange={(e) => set("category_id", e.target.value || null)} data-testid="editor-category-select">
                <option value="">No category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="admin-label">Tags</label>
              <div className="flex gap-2">
                <input
                  className="admin-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                  placeholder="Type + Enter"
                  data-testid="editor-tag-input"
                />
                <button className="cat-pill shrink-0" onClick={addTag} data-testid="editor-add-tag">Add</button>
              </div>
              {form.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {form.tags.map((t) => (
                    <span key={t} className="text-xs text-[#A1A1AA] border border-[#27272A] px-2.5 py-1 inline-flex items-center gap-1.5">
                      #{t}
                      <button onClick={() => set("tags", form.tags.filter((x) => x !== t))} className="hover:text-red-400" data-testid={`remove-tag-${t}`}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
